import { useMemo, useRef, useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import ActionCheckbox from "../../components/common/ActionCheckbox";
import { useStore } from "../../context/StoreContext";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useGetBundlesQuery,
  useGetBundleTypesQuery,
  useCreateBundleMutation,
  useUpdateBundleMutation,
  useDeleteBundleMutation,
} from "../../store/api";

const getBundleOrderKey = (bundle, index = 0) => {
  const bundleId = bundle?.bundleId ?? bundle?.id;
  if (bundleId !== undefined && bundleId !== null && bundleId !== "") {
    return `id:${String(bundleId)}`;
  }
  return `tmp:${bundle?.name || bundle?.bundleName || "row"}:${index}`;
};

const Bundles = () => {
  const { selectedStoreId } = useStore();
  const {
    data: bundles,
    isLoading,
    isError,
    error,
  } = useGetBundlesQuery(selectedStoreId, { skip: !selectedStoreId });
  const { data: bundleTypes } = useGetBundleTypesQuery();

  const [createBundle, createState] = useCreateBundleMutation();
  const [updateBundle, updateState] = useUpdateBundleMutation();
  const [deleteBundle, deleteState] = useDeleteBundleMutation();

  const [form, setForm] = useState({
    name: "",
    description: "",
    bundleType: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    bundleId: null,
    bundleName: "",
  });

  const getBundleId = (bundle) => bundle?.bundleId ?? bundle?.id;
  const bundleOrderRef = useRef(new Map());
  const bundleOrderSeqRef = useRef(0);

  const bundlesList = useMemo(() => {
    const source = Array.isArray(bundles) ? bundles : [];

    source.forEach((bundle, index) => {
      const orderKey = getBundleOrderKey(bundle, index);

      if (!bundleOrderRef.current.has(orderKey)) {
        bundleOrderRef.current.set(orderKey, bundleOrderSeqRef.current++);
      }
    });

    return [...source]
      .map((bundle, index) => ({ bundle, index }))
      .sort((a, b) => {
        const aKey = getBundleOrderKey(a.bundle, a.index);
        const bKey = getBundleOrderKey(b.bundle, b.index);

        const aOrder =
          bundleOrderRef.current.get(aKey) ?? Number.MAX_SAFE_INTEGER;
        const bOrder =
          bundleOrderRef.current.get(bKey) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      })
      .map(({ bundle }) => bundle);
  }, [bundles]);
  const {
    page,
    totalPages,
    totalCount,
    pageSize,
    paginatedItems: paginatedBundles,
    setPage,
    setPageSize,
  } = useClientPagination(bundlesList, { pageSize: 10 });

  const bundleTypesList = Array.isArray(bundleTypes) ? bundleTypes : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      alert("Please select a store first");
      return;
    }

    try {
      if (editingId) {
        await updateBundle({
          storeId: selectedStoreId,
          bundleId: editingId,
          payload: form,
        }).unwrap();
        setEditingId(null);
      } else {
        await createBundle({
          storeId: selectedStoreId,
          payload: form,
        }).unwrap();
      }
      setForm({ name: "", description: "", bundleType: "", isActive: true });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (bundle) => {
    setEditingId(getBundleId(bundle));
    setForm({
      name: bundle.name || bundle.bundleName || "",
      description: bundle.description || "",
      bundleType: bundle.bundleType || "",
      isActive: bundle.isActive !== undefined ? bundle.isActive : true,
    });
    setShowForm(true);
  };

  const handleDelete = (bundle) => {
    setDeleteModal({
      isOpen: true,
      bundleId: getBundleId(bundle),
      bundleName: bundle.name || bundle.bundleName || "this bundle",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteBundle({
        storeId: selectedStoreId,
        bundleId: deleteModal.bundleId,
      }).unwrap();
    } catch (err) {
      // error handled by UI
    } finally {
      setDeleteModal({
        isOpen: false,
        bundleId: null,
        bundleName: "",
      });
    }
  };

  const handleToggleActive = async (bundle) => {
    const bundleId = getBundleId(bundle);
    const isActive =
      bundle.isActive !== undefined && bundle.isActive !== null
        ? bundle.isActive
        : true;
    try {
      await updateBundle({
        storeId: selectedStoreId,
        bundleId,
        payload: {
          name: bundle.name || bundle.bundleName || "",
          description: bundle.description || "",
          bundleType: bundle.bundleType || "",
          isActive: !isActive,
        },
      }).unwrap();
    } catch (err) {
      // error handled by UI
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({ name: "", description: "", bundleType: "", isActive: true });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "bundleType", label: "Bundle Type" },
    { key: "description", label: "Description" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = paginatedBundles.map((bundle, index) => {
    const bundleId = getBundleId(bundle);
    const rowId =
      bundleId !== undefined && bundleId !== null && bundleId !== ""
        ? String(bundleId)
        : `bundle-${bundle.name || bundle.bundleName || "row"}-${index}`;
    const description = bundle.description || "-";
    const isActive =
      bundle.isActive !== undefined && bundle.isActive !== null
        ? bundle.isActive
        : true;

    return {
      id: rowId,
      name: bundle.name || bundle.bundleName || "-",
      bundleType: bundle.bundleType || "-",
      description:
        description.length > 80
          ? `${description.slice(0, 80)}...`
          : description,
      status: (
        <div className="flex items-center gap-2">
          <ActionCheckbox
            checked={isActive}
            label={isActive ? "Active" : "Inactive"}
            onChange={() => handleToggleActive(bundle)}
          />
          <span className="text-sm text-slate-700">
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEdit(bundle)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDelete(bundle)}
          />
        </div>
      ),
    };
  });

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Bundles"
          subtitle="Manage meal bundles and combos."
        />
        <Card>
          <Alert tone="warning">Please select a store to manage bundles.</Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bundles"
        subtitle="Create and manage meal bundles."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Bundle
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingId ? "Edit Bundle" : "Add Bundle"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Bundle Name"
                name="name"
                placeholder="Family Meal Deal"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Select
                label="Bundle Type"
                name="bundleType"
                value={form.bundleType}
                onChange={handleChange}
                required
              >
                <option value="">Select a type</option>
                {bundleTypesList.map((type) => (
                  <option key={type.code || type} value={type.code || type}>
                    {type.label || type}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Textarea
                label="Description"
                name="description"
                placeholder="Perfect for the whole family..."
                value={form.description}
                onChange={handleChange}
              />
              <Checkbox
                label="Active"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
              />
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Bundle {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save bundle."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Bundle
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            All Bundles
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading bundles...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load bundles."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No bundles yet."
                />
                {totalPages > 1 && (
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              )}
              </>
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            bundleId: null,
            bundleName: "",
          })
        }
        title="Delete Bundle"
        confirmText="Delete"
        onConfirm={confirmDelete}
        isLoading={deleteState.isLoading}
      >
        <p>
          Are you sure you want to delete <strong>{deleteModal.bundleName}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default Bundles;
